import React, { useCallback, useEffect, useRef, useState } from 'react';
import { hsAuthCodes } from './api';

// =============================================================================
// HsAuthCodePanel (5.41.0) — live HealthSherpa / CMS verification codes on the HealthSherpa tab.
// Telecom 1.8.0 copies those texts (matched by wording, any sender) into telecom.auth_codes; the backend
// returns the last 5 minutes. Polls every 4 s ONLY while this tab is visible; errors and logouts are silent.
// Slides in from the right when a code is live (z-index 1200: above the plan drawer (950) and the full-screen plan views (1000)). Dismiss is local to this
// browser (never deletes). A card disappears 5 minutes after the text arrived. Newest on top.
// Audit (server side): first display per user per code = "viewed"; every Copy = "copied" — id + provider only.
// =============================================================================

export const POLL_MS = 4000;
export const LIFETIME_MS = 5 * 60 * 1000;
const DISMISS_KEY = 'hsAuthCodes.dismissed';

export function liveCodes(codes, dismissed, nowMs) {
  return (codes || [])
    .filter((c) => !dismissed.has(String(c.id)))
    .filter((c) => {
      const t = Date.parse(c.received_at);
      return Number.isFinite(t) && nowMs - t < LIFETIME_MS;
    })
    .sort((a, b) => Date.parse(b.received_at) - Date.parse(a.received_at) || b.id - a.id);
}

export function mmss(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

function loadDismissed() {
  try {
    const v = JSON.parse(window.localStorage.getItem(DISMISS_KEY) || '[]');
    return new Set(Array.isArray(v) ? v.map(String) : []);
  } catch (_e) { return new Set(); }
}

function saveDismissed(set) {
  // Keep the list short: ids only, last 50.
  try { window.localStorage.setItem(DISMISS_KEY, JSON.stringify([...set].slice(-50))); } catch (_e) { /* ignore */ }
}

// Same clipboard approach as Main.js copyAuthCode (navigator.clipboard, textarea fallback).
async function copyText(code) {
  if (!code) return false;
  try {
    await navigator.clipboard.writeText(code);
    return true;
  } catch (_e) {
    try {
      const ta = document.createElement('textarea');
      ta.value = code;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      return true;
    } catch (_e2) { return false; }
  }
}

export default function HsAuthCodePanel({ darkMode = true }) {
  const [codes, setCodes] = useState([]);
  const [dismissed, setDismissed] = useState(loadDismissed);
  const [now, setNow] = useState(() => Date.now());
  const [copiedId, setCopiedId] = useState(null);
  const viewedRef = useRef(new Set());

  const poll = useCallback(async () => {
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
    try {
      const r = await hsAuthCodes.recent();
      setCodes(Array.isArray(r?.data?.codes) ? r.data.codes : []);
    } catch (_e) { /* silent: logged out / offline / server busy */ }
  }, []);

  useEffect(() => {
    poll();
    const id = setInterval(poll, POLL_MS);
    const onVis = () => { if (document.visibilityState === 'visible') poll(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVis); };
  }, [poll]);

  const live = liveCodes(codes, dismissed, now);
  const open = live.length > 0;

  // 1-second tick only while something is showing (countdown + expiry).
  useEffect(() => {
    if (!open) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [open]);
  useEffect(() => { setNow(Date.now()); }, [codes]);

  // "viewed" audit: once per code per page load (the server keeps only the first per user).
  const liveIds = live.map((c) => c.id).join(',');
  useEffect(() => {
    live.forEach((c) => {
      if (viewedRef.current.has(c.id)) return;
      viewedRef.current.add(c.id);
      hsAuthCodes.viewed(c.id).catch(() => {});
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveIds]);

  const dismiss = (id) => {
    setDismissed((prev) => {
      const next = new Set(prev);
      next.add(String(id));
      saveDismissed(next);
      return next;
    });
  };

  const copy = async (c) => {
    if (await copyText(c.code)) {
      setCopiedId(c.id);
      setTimeout(() => setCopiedId((v) => (v === c.id ? null : v)), 1500);
    }
    hsAuthCodes.copied(c.id).catch(() => {});
  };

  const bg = darkMode ? '#1f2a33' : '#ffffff';
  const border = darkMode ? '#3b5568' : '#c9d6e0';
  const fg = darkMode ? '#f0f0f0' : '#1a1a1a';
  const sub = darkMode ? '#9fb3c2' : '#5a6b78';

  return (
    <div data-testid="hs-auth-panel" aria-live="polite" aria-hidden={!open}
      style={{ position: 'fixed', top: 72, right: 0, width: 290, maxWidth: '92vw', zIndex: 1200,
        transform: open ? 'translateX(0)' : 'translateX(110%)', transition: 'transform 220ms ease-out',
        pointerEvents: open ? 'auto' : 'none', display: 'flex', flexDirection: 'column', gap: 8, padding: '0 10px' }}>
      {live.map((c) => {
        const age = now - Date.parse(c.received_at);
        return (
          <div key={c.id} data-testid="hs-auth-card"
            style={{ background: bg, color: fg, border: `1px solid ${border}`, borderRadius: 10, padding: '10px 12px',
              boxShadow: '0 6px 18px rgba(0,0,0,0.35)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, color: sub }}>
              <span>🔑 {c.provider} code</span>
              <button data-testid="hs-auth-dismiss" onClick={() => dismiss(c.id)} title="Dismiss (this browser only)"
                style={{ background: 'transparent', border: 'none', color: sub, cursor: 'pointer', fontSize: 14 }}>✕</button>
            </div>
            <div data-testid="hs-auth-code"
              style={{ fontFamily: 'monospace', fontSize: 30, fontWeight: 700, letterSpacing: 4, margin: '4px 0 6px' }}>
              {c.code}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, color: sub }}>
              <span data-testid="hs-auth-age">{mmss(age)} ago · gone in {mmss(LIFETIME_MS - age)}</span>
              <button data-testid="hs-auth-copy" onClick={() => copy(c)}
                style={{ padding: '4px 12px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 12,
                  background: copiedId === c.id ? '#3fa45b' : '#1d628a', color: '#fff' }}>
                {copiedId === c.id ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
